import type { Routes } from '@angular/router';
import { staffAcceptedGuard, staffAuthGuard, staffManagerGuard } from '@sbe-spinoff-client/staff-oidc-auth';

/**
 * Phase 8 cutover: customToken 기반 `/auth/exchange` 라우트 폐기.
 * 미인증 시 `staffAuthGuard` 가 staff-api 의 `/staff/auth/sso/<provider>/authorize-redirect` 로 navigate.
 */
export const appRoutes: Routes = [
  // 로그아웃 후 도착하는 페이지 — guard 없음 (guard 있으면 logout 직후 루프 발생).
  // 헤더는 항상 보인다(app.ts) — nav/로그아웃은 isAuthenticated()가 false라 자동으로 숨겨진다.
  {
    path: 'signed-out',
    loadComponent: () =>
      import('./features/signed-out/signed-out.component').then(
        (m) => m.SignedOutComponent,
      ),
  },
  {
    // 인증 후 영역 — 셸은 app.ts(앱 루트)에 있다. 이 그룹은 pathless(component 없음)로만
    // 남는다(2026-08-11 루트 배치 마이그레이션, layout/shell/app-shell.component.ts 삭제).
    path: '',
    canActivate: [staffAuthGuard],
    children: [
      {
        // 내게 온 초대 수락 — **수락 전 staff 가 도착하는 화면**이라 `staffAcceptedGuard` 밖에 둔다(안에 두면 리다이렉트 루프).
        // 서버는 이 라우트에 scope 를 요구하지 않고(출입만) 초대 번호와 내 번호가 같은지를 직접 확인한다.
        path: 'my-invitations',
        loadComponent: () =>
          import('@sbe-spinoff-client/staff-members-admin').then(
            (m) => m.StaffInvitationInboxComponent,
          ),
      },
      {
        // 초대를 수락한 staff(또는 초대가 필요 없는 staff) 영역 — 수락 전에는 `/my-invitations` 로 보낸다. UI 보호일 뿐 서버는 수락 전 scope 요구를 독립적으로 거부한다.
        path: '',
        canActivate: [staffAcceptedGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'originators' },
          {
            path: 'originators',
            loadComponent: () =>
              import('./features/originators/originators.component').then(
                (m) => m.OriginatorsComponent,
              ),
          },
          {
            path: 'messages',
            loadComponent: () =>
              import('./features/messages/messages.component').then(
                (m) => m.MessagesComponent,
              ),
          },
          {
            path: 'two-factor-auth-history',
            loadComponent: () =>
              import(
                './features/two-factor-auth-history/two-factor-auth-history.component'
              ).then((m) => m.TwoFactorAuthHistoryComponent),
          },
          {
            path: 'org-provider-link',
            loadComponent: () =>
              import(
                './features/org-provider-link/org-provider-link.component'
              ).then((m) => m.OrgProviderLinkComponent),
          },
          {
            // staff 권한 관리 — 관리 역할(Owner·Admin)만. 가드는 UI 보호일 뿐이고 서버가 `StaffManagerGuard` 로 독립 거부한다.
            // `canMatch` 가 아니라 `canActivate` 다: 부모 그룹의 `staffAuthGuard` 가 `me` 를 불러온 뒤 판정해야 콜드 진입에서 관리자가 튕기지 않는다.
            path: 'staff-members',
            canActivate: [staffManagerGuard],
            loadComponent: () =>
              import('@sbe-spinoff-client/staff-members-admin').then(
                (m) => m.StaffMemberListComponent,
              ),
          },
          {
            // staff 초대 관리 — 관리 역할(Owner·Admin)만(UI 보호일 뿐, 서버가 `StaffManagerGuard` 로 독립 거부).
            path: 'staff-invitations',
            canActivate: [staffManagerGuard],
            loadComponent: () =>
              import('@sbe-spinoff-client/staff-members-admin').then(
                (m) => m.StaffInvitationListComponent,
              ),
          },
        ],
      },
    ],
  },
];
